import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Package, LogOut, Shield } from 'lucide-react';
import { useCart } from '../context/CartContext';

const SideMenu = ({ isOpen, onClose, onSelectCategory }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerAuthSync } = useCart();

  // Local Filter Tracking States
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [maxPrice, setMaxPrice] = useState(5000);
  
  // Active User State
  const [activeUser, setActiveUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('modische_user');
    if (savedUser && savedUser !== 'null' && savedUser !== 'undefined') {
      try { 
        setActiveUser(JSON.parse(savedUser)); 
      } catch (e) { 
        setActiveUser(null); 
      }
    } else {
      setActiveUser(null); 
    }
  }, [isOpen, location]);

  if (!isOpen) return null;

  const handleCategoryClick = (gender, fitStyle) => {
    onClose(); 
    const URLPath = `${gender.toLowerCase()}-${fitStyle.toLowerCase().replace(' ', '')}`;
    navigate(`/collection/${URLPath}`);
  };

  const handleSignInRoute = () => {
    onClose();
    navigate(activeUser ? '/' : '/auth'); 
  };

  const handleLogout = () => {
    localStorage.removeItem('modische_token');
    localStorage.removeItem('modische_user');
    setActiveUser(null);
    if (triggerAuthSync) triggerAuthSync();
    onClose();
    navigate('/');
  };

  return (
    <div className="fixed inset-0 z-[150] flex justify-start animate-fade-in">
      {/* Dimmed Background Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Side Menu Drawer */}
      <div className="relative w-full max-w-sm bg-[#2C1E12] text-[#F5F5DC] h-full p-8 overflow-y-auto shadow-2xl flex flex-col justify-between z-10 border-r border-[#E5D3B3]/10 transition-transform duration-500 transform translate-x-0">
        <div>
          {/* Top Header */}
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-[#F5F5DC]/10">
            <h2 className="font-serif text-xl tracking-wide">Browse & Filter</h2>
            <button onClick={onClose} className="p-1 hover:opacity-50 transition bg-transparent border-none text-[#F5F5DC] cursor-pointer">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Profile / Status */}
          <div className="mb-6 pb-6 border-b border-[#F5F5DC]/10">
            <div 
              onClick={handleSignInRoute}
              className="flex items-center space-x-3 group cursor-pointer hover:opacity-80 transition"
            >
              <div className="w-7 h-7 flex items-center justify-center bg-[#E5D3B3]/10 rounded-full text-xs font-mono font-black text-[#E5D3B3]">
                {activeUser && activeUser.name ? activeUser.name[0].toUpperCase() : '👤'}
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-widest block">
                  {activeUser && activeUser.name ? `Hello, ${activeUser.name.split(' ')[0]}` : 'Sign In / Register'}
                </span>
                {activeUser?.email && (
                  <span className="text-[10px] text-[#E5D3B3]/60 font-mono block truncate max-w-[200px]">
                    {activeUser.email}
                  </span>
                )}
              </div>
            </div>

            {/* Authenticated Actions: Order History, Admin, Logout */}
            {activeUser && (
              <div className="mt-4 pt-4 border-t border-[#F5F5DC]/5 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate('/my-orders');
                  }}
                  className="w-full flex items-center gap-2.5 py-2 px-3 bg-[#E5D3B3]/5 hover:bg-[#E5D3B3]/15 text-[#F5F5DC] rounded-sm text-[10px] uppercase tracking-widest font-bold transition border border-[#E5D3B3]/10 cursor-pointer"
                >
                  <Package size={13} className="text-[#E5D3B3]" /> My Orders
                </button>

                {activeUser.isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate('/admin');
                    }}
                    className="w-full flex items-center gap-2.5 py-2 px-3 bg-red-950/40 hover:bg-red-900/50 text-red-200 rounded-sm text-[10px] uppercase tracking-widest font-bold transition border border-red-800/30 cursor-pointer"
                  >
                    <Shield size={13} className="text-red-300" /> Admin Portal
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 py-2 px-3 bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-red-200 rounded-sm text-[10px] uppercase tracking-widest font-bold transition border border-red-500/20 cursor-pointer"
                >
                  <LogOut size={13} /> Log Out
                </button>
              </div>
            )}
          </div>

          {/* GENDER FILTER MODULE */}
          <div className="space-y-3 mb-8">
            <h4 className="text-[10px] uppercase tracking-[0.2em] opacity-40 font-bold">Gender</h4>
            <div className="flex gap-2">
              {['Men', 'Women', 'Unisex'].map((g) => (
                <button
                  key={g}
                  onClick={() => setSelectedGender(selectedGender === g ? '' : g)}
                  className={`flex-1 py-2 text-xs uppercase tracking-wider rounded-full border transition-all ${
                    selectedGender === g 
                      ? 'bg-[#F5F5DC] text-[#2C1E12] border-transparent font-bold shadow-md' 
                      : 'border-[#F5F5DC]/20 text-[#F5F5DC] hover:border-[#F5F5DC]'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* DYNAMIC CATEGORIES FIT MODULE */}
          {(!selectedGender || selectedGender === 'Men') && (
            <div className="space-y-2 mb-6">
              <h4 className="text-[10px] uppercase tracking-[0.2em] opacity-40 font-bold">Men</h4>
              <button onClick={() => handleCategoryClick('Men', 'Regular Fit')} className="block text-sm font-medium hover:opacity-70 transition pl-1 bg-transparent border-none text-[#F5F5DC] cursor-pointer">Regular Fit</button>
              <button onClick={() => handleCategoryClick('Men', 'Oversized')} className="block text-sm font-medium hover:opacity-70 transition pl-1 bg-transparent border-none text-[#F5F5DC] cursor-pointer">Oversized</button>
            </div>
          )}

          {(!selectedGender || selectedGender === 'Women') && (
            <div className="space-y-2 mb-8">
              <h4 className="text-[10px] uppercase tracking-[0.2em] opacity-40 font-bold">Women</h4>
              <button onClick={() => handleCategoryClick('Women', 'Regular Fit')} className="block text-sm font-medium hover:opacity-70 transition pl-1 bg-transparent border-none text-[#F5F5DC] cursor-pointer">Regular Fit</button>
              <button onClick={() => handleCategoryClick('Women', 'Oversized')} className="block text-sm font-medium hover:opacity-70 transition pl-1 bg-transparent border-none text-[#F5F5DC] cursor-pointer">Oversized</button>
            </div>
          )}

          {/* SIZE MULTI-SELECTOR COMPONENT */}
          <div className="space-y-3 mb-10">
            <h4 className="text-[10px] uppercase tracking-[0.2em] opacity-40 font-bold">Size</h4>
            <div className="grid grid-cols-4 gap-2">
              {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedSize(selectedSize === s ? '' : s)}
                  className={`aspect-square text-xs font-mono border transition rounded-sm cursor-pointer ${
                    selectedSize === s 
                      ? 'bg-[#F5F5DC] text-[#2C1E12] border-transparent font-bold' 
                      : 'border-[#F5F5DC]/10 text-[#F5F5DC] hover:border-[#F5F5DC]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* PRICE RANGE SLIDER SYSTEM */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-[10px] uppercase tracking-[0.2em] opacity-40 font-bold">Price Range</h4>
              <span className="text-xs font-mono tracking-tight text-[#E5D3B3] font-bold">₹0 — ₹{maxPrice}</span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="5000" 
              step="100" 
              value={maxPrice} 
              onChange={(e) => setMaxPrice(Number(e.target.value))}
              className="w-full h-[2px] bg-zinc-600 appearance-none cursor-pointer accent-[#F5F5DC]" 
            />
          </div>
        </div>

        {/* BOTTOM FILTER EXECUTE ACTION CONSOLE */}
        {(selectedGender || selectedSize || maxPrice < 5000) && (
          <div className="pt-6 border-t border-[#F5F5DC]/10 mt-8 space-y-2">
            <button
              onClick={() => {
                onClose();
                const queries = `?gender=${selectedGender}&size=${selectedSize}&price=${maxPrice}`;
                navigate(`/collection/search${queries}`);
              }}
              className="w-full py-3.5 bg-[#F5F5DC] text-[#2C1E12] text-[10px] uppercase tracking-widest font-bold hover:opacity-90 transition border-none cursor-pointer"
            >
              Apply Filter Direct
            </button>
            <button
              onClick={() => {
                setSelectedGender('');
                setSelectedSize('');
                setMaxPrice(5000);
              }}
              className="w-full py-2 text-[9px] uppercase tracking-widest opacity-50 text-white font-mono hover:opacity-100 transition bg-transparent border-none cursor-pointer"
            >
              Reset Matrices
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SideMenu;